import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { PageHead } from "@/components/PageHead";
import { SearchInput } from "@/components/inputs";

/** Reference — rendered from the LIVE /openapi.json of this deployment.
 *  Lean by design: no swagger-ui bundle (600KB+ and off-brand); we render
 *  method/path/params and EXAMPLES generated from the schemas, which is
 *  what an integrator actually needs. */

type Schema = Record<string, any>;

interface Op {
  method: string;
  path: string;
  summary: string;
  description: string;
  tag: string;
  parameters: { name: string; in: string; required?: boolean; schema?: Schema }[];
  requestBody?: Schema;      // resolved media schema
  response?: Schema;         // resolved 200 media schema
  example200?: unknown;      // literal example from the spec (wins over schema-derived)
}

const METHOD_COLOR: Record<string, string> = {
  get: "var(--info)", post: "var(--ok)", put: "var(--warn)",
  patch: "var(--warn)", delete: "var(--bad)",
};

/** Resolve $ref against components and build a compact example value. */
function example(schema: Schema | undefined, components: Schema, depth = 0): unknown {
  if (!schema || depth > 5) return null;
  if (schema.$ref) {
    const name = String(schema.$ref).split("/").pop()!;
    return example(components[name], components, depth + 1);
  }
  if (schema.anyOf) return example(schema.anyOf.find((s: Schema) => s.type !== "null") ?? schema.anyOf[0], components, depth + 1);
  if (schema.allOf) return example(schema.allOf[0], components, depth + 1);
  if (schema.enum) return schema.enum[0];
  switch (schema.type) {
    case "object": {
      const props = schema.properties ?? {};
      const keys = Object.keys(props);
      if (keys.length === 0) return schema.additionalProperties ? { "…": "…" } : {};
      const out: Record<string, unknown> = {};
      for (const k of keys) out[k] = example(props[k], components, depth + 1);
      return out;
    }
    case "array": return [example(schema.items, components, depth + 1)];
    case "integer": case "number": return 0;
    case "boolean": return true;
    case "string": return schema.format === "date-time" ? "2026-08-19T12:00:00+00:00" : "string";
    default: return null;
  }
}

function EndpointRow({ op, components }: { op: Op; components: Schema }) {
  const [open, setOpen] = useState(false);
  const color = METHOD_COLOR[op.method] ?? "var(--t-mute)";
  return (
    <div style={{ borderBottom: "1px solid var(--line)" }}>
      <button onClick={() => setOpen((o) => !o)}
        style={{ display: "flex", gap: 12, alignItems: "center", width: "100%", padding: "10px 4px",
          background: "none", border: "none", cursor: "pointer", textAlign: "left", color: "inherit" }}>
        <span className="mono" style={{ color, fontWeight: 800, fontSize: 11, width: 52, flexShrink: 0, textTransform: "uppercase" }}>
          {op.method}
        </span>
        <span className="mono" style={{ fontSize: 12.5, color: "var(--t-bright)" }}>{op.path}</span>
        <span style={{ fontSize: 12, color: "var(--t-mute)", marginLeft: "auto", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {op.summary}
        </span>
        <span style={{ color: "var(--t-dim)" }}>{open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <div style={{ padding: "4px 4px 16px 64px", fontSize: 12.5 }}>
          {op.description && (
            <p style={{ whiteSpace: "pre-wrap", color: "var(--t-mute)", margin: "0 0 10px", lineHeight: 1.6 }}>
              {op.description}
            </p>
          )}
          {op.parameters.length > 0 && (
            <>
              <div style={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: ".6px", color: "var(--t-mute)", margin: "8px 0 4px" }}>Parameters</div>
              {op.parameters.map((p) => (
                <div key={p.in + p.name} className="mono" style={{ padding: "2px 0" }}>
                  {p.name}
                  <span style={{ color: "var(--t-dim)" }}> ({p.in}{p.required ? ", required" : ""})</span>
                  <span className="muted"> — {p.schema?.type ?? "any"}</span>
                </div>
              ))}
            </>
          )}
          {op.requestBody && (
            <>
              <div style={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: ".6px", color: "var(--t-mute)", margin: "10px 0 4px" }}>Request body</div>
              <pre className="mono" style={{ background: "var(--well)", border: "1px solid var(--line)", borderRadius: 8, padding: 10, fontSize: 11.5, overflowX: "auto", margin: 0 }}>
                {JSON.stringify(example(op.requestBody, components), null, 2)}
              </pre>
            </>
          )}
          {(op.example200 !== undefined || op.response) && (
            <>
              <div style={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: ".6px", color: "var(--t-mute)", margin: "10px 0 4px" }}>Response 200</div>
              <pre className="mono" style={{ background: "var(--well)", border: "1px solid var(--line)", borderRadius: 8, padding: 10, fontSize: 11.5, overflowX: "auto", margin: 0 }}>
                {JSON.stringify(op.example200 !== undefined ? op.example200 : example(op.response, components), null, 2)}
              </pre>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function ApiReferencePage() {
  const [filter, setFilter] = useState("");
  const q = useQuery({
    queryKey: ["openapi"],
    queryFn: () => fetch("/openapi.json").then((r) => r.json()),
    staleTime: 5 * 60_000,
  });

  const { groups, components } = useMemo(() => {
    const spec = q.data;
    if (!spec) return { groups: [] as [string, Op[]][], components: {} };
    const components = spec.components?.schemas ?? {};
    const ops: Op[] = [];
    for (const [path, methods] of Object.entries<Schema>(spec.paths ?? {})) {
      for (const [method, def] of Object.entries<Schema>(methods)) {
        if (!["get", "post", "put", "patch", "delete"].includes(method)) continue;
        ops.push({
          method, path,
          summary: def.summary ?? "",
          description: def.description ?? "",
          tag: def.tags?.[0] ?? "other",
          parameters: def.parameters ?? [],
          requestBody: def.requestBody?.content?.["application/json"]?.schema,
          response: def.responses?.["200"]?.content?.["application/json"]?.schema,
          example200: def.responses?.["200"]?.content?.["application/json"]?.example,
        });
      }
    }
    const f = filter.trim().toLowerCase();
    const shown = f
      ? ops.filter((o) => o.path.toLowerCase().includes(f) || o.summary.toLowerCase().includes(f) || o.tag.includes(f))
      : ops;
    const byTag = new Map<string, Op[]>();
    for (const o of shown) {
      if (!byTag.has(o.tag)) byTag.set(o.tag, []);
      byTag.get(o.tag)!.push(o);
    }
    return { groups: [...byTag.entries()], components };
  }, [q.data, filter]);

  return (
    <div className="mpage on">
      <PageHead eyebrow="API · Reference" title="API reference"
        desc="Rendered live from this deployment's /openapi.json — what you see here is exactly what is running." />

      <SearchInput placeholder="Filter by path / summary / module…" value={filter}
        onChange={(e) => setFilter(e.target.value)} style={{ width: 320, marginBottom: 14 }} />

      {q.isLoading && <div className="panel panel-pad muted">Loading schema…</div>}
      {q.isError && <div className="panel panel-pad" style={{ color: "var(--bad)" }}>Couldn't load /openapi.json.</div>}

      {groups.map(([tag, ops]) => (
        <div key={tag} style={{ marginBottom: 18 }}>
          <div className="section-label">{tag} · {ops.length}</div>
          <div className="panel" style={{ padding: "2px 14px" }}>
            {ops.map((o) => <EndpointRow key={o.method + o.path} op={o} components={components} />)}
          </div>
        </div>
      ))}
    </div>
  );
}
